import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';

import { TableSortArgs } from '../../components/TableV2';
import { INSURANCE_INITIAL_STATE } from '../constants/insurance.constants';
import {
    ICreateProviderPayload,
    ICreatePlanPayload,
    ICreateBeneficiaryPayload,
    IAssignFacilitiesPayload,
    ICheckBenefitEligibilityPayload,
} from '../types/Insurance.types';

export const getProviderList = createAsyncThunk(
    'insurance/getProviderList',
    async (data: TableSortArgs) => {
        const response = await axios.post('/Insurance/GetProviderList', data);
        return response.data;
    },
);

export const createProvider = createAsyncThunk(
    'insurance/createProvider',
    async (data: ICreateProviderPayload) => {
        const response = await axios.post('/Insurance/CreateProvider', data);
        return response.data;
    },
);

export const getProviderById = createAsyncThunk(
    'insurance/getProviderById',
    async (id: number) => {
        const response = await axios.get(`/Insurance/GetProviderById/${id}`);
        return response.data;
    },
);

export const updateProvider = createAsyncThunk(
    'insurance/updateProvider',
    async (data: ICreateProviderPayload) => {
        const response = await axios.post('/Insurance/UpdateProvider', data);
        return response.data;
    },
);

export const getServiceProviders = createAsyncThunk(
    'insurance/getServiceProviders',
    async (providerId: number) => {
        const response = await axios.get(
            `/Insurance/GetServiceProviders/${providerId}`,
        );
        return response.data;
    },
);

export const assignFacilitiesToProvider = createAsyncThunk(
    'insurance/assignFacilitiesToProvider',
    async (data: IAssignFacilitiesPayload) => {
        const response = await axios.post(
            '/Insurance/AssignFacilitiesToProvider',
            data,
        );
        return response.data;
    },
);

export const getPlanList = createAsyncThunk(
    'insurance/getPlanList',
    async (data: TableSortArgs) => {
        const response = await axios.post('/Insurance/GetPlanList', data);
        return response.data;
    },
);

export const createPlan = createAsyncThunk(
    'insurance/createPlan',
    async (data: ICreatePlanPayload) => {
        const response = await axios.post('/Insurance/CreatePlan', data);
        return response.data;
    },
);

export const getPlanById = createAsyncThunk(
    'insurance/getPlanById',
    async (id: number) => {
        const response = await axios.get(`/Insurance/GetPlanById/${id}`);
        return response.data;
    },
);

export const updatePlan = createAsyncThunk(
    'insurance/updatePlan',
    async (data: ICreatePlanPayload) => {
        const response = await axios.post('/Insurance/UpdatePlan', data);
        return response.data;
    },
);

export const getBeneficiaryList = createAsyncThunk(
    'insurance/getBeneficiaryList',
    async (data: TableSortArgs) => {
        const response = await axios.post(
            '/Insurance/GetBeneficiaryList',
            data,
        );
        return response.data;
    },
);

export const createBeneficiary = createAsyncThunk(
    'insurance/createBeneficiary',
    async (data: ICreateBeneficiaryPayload) => {
        const response = await axios.post('/Insurance/CreateBeneficiary', data);
        return response.data;
    },
);

export const getBeneficiaryById = createAsyncThunk(
    'insurance/getBeneficiaryById',
    async (id: number) => {
        const response = await axios.get(`/Insurance/GetBeneficiaryById/${id}`);
        return response.data;
    },
);

export const updateBeneficiary = createAsyncThunk(
    'insurance/updateBeneficiary',
    async (data: ICreateBeneficiaryPayload) => {
        const response = await axios.post('/Insurance/UpdateBeneficiary', data);
        return response.data;
    },
);

export const bulkUploadBeneficiaries = createAsyncThunk(
    'insurance/bulkUploadBeneficiaries',
    async ({
        planId,
        data,
    }: {
        planId: number;
        data: ICreateBeneficiaryPayload[];
    }) => {
        const response = await axios.post(
            `/Insurance/BulkUploadBeneficiaries/${planId}`,
            data,
        );
        return response.data;
    },
);

export const getBeneficiariesByPlan = createAsyncThunk(
    'insurance/getBeneficiariesByPlan',
    async (planId: number) => {
        const response = await axios.get(
            `/Insurance/GetBeneficiariesByPlan/${planId}`,
        );
        return response.data;
    },
);

export const checkBenefitEligibilityByEMR = createAsyncThunk(
    'insurance/checkBenefitEligibilityByEMR',
    async (data: ICheckBenefitEligibilityPayload) => {
        const params = new URLSearchParams({
            EMRNo: data.emrNo,
            billAmount: String(data.billAmount),
        });
        const response = await axios.post(
            `/Insurance/CheckBenefitEligibilityByEMR?${params.toString()}`,
        );
        return response.data;
    },
);

export const getInsuranceProviderDashboard = createAsyncThunk(
    'insurance/getInsuranceProviderDashboard',
    async () => {
        const response = await axios.get('/Insurance/User/Dashboard');
        return response.data;
    },
);

export const getInsuranceProviderBeneficiaries = createAsyncThunk(
    'insurance/getInsuranceProviderBeneficiaries',
    async (data: TableSortArgs) => {
        const response = await axios.post('/Insurance/User/Beneficiaries', data);
        return response.data;
    },
);

export const getInsuranceProviderPlans = createAsyncThunk(
    'insurance/getInsuranceProviderPlans',
    async (data: TableSortArgs) => {
        const response = await axios.post('/Insurance/User/Plans', data);
        return response.data;
    },
);

export const getInsuranceProviderBeneficiaryUsage = createAsyncThunk(
    'insurance/getInsuranceProviderBeneficiaryUsage',
    async (data: TableSortArgs) => {
        const response = await axios.post('/Insurance/User/BeneficiaryUsage', data);
        return response.data;
    },
);

export const insurance = createSlice({
    name: 'insurance',
    initialState: INSURANCE_INITIAL_STATE,
    reducers: {
        onInsurancePageChange: (state, action) => {
            state.pageProps = action.payload;
        },
        onChangeInsuranceType: (state, action) => {
            state.insuranceType = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getProviderList.pending, (state) => {
                state.loading = 'pending';
            })
            .addCase(getProviderList.fulfilled, (state, action) => {
                state.loading = 'succeeded';
                // API returns data directly, not wrapped in result
                const responseData = action.payload?.result || action.payload;
                state.providerData = {
                    list: responseData?.list || [],
                    totalPages: responseData?.totalPages || 0,
                    totalRecords: responseData?.totalRecords || 0,
                };
                state.error = null;
            })
            .addCase(getProviderList.rejected, (state, action) => {
                state.loading = 'failed';
                state.error = action.error.message ?? 'An error occurred.';
            })
            .addCase(createProvider.pending, (state) => {
                state.loading = 'pending';
            })
            .addCase(createProvider.fulfilled, (state) => {
                state.loading = 'succeeded';
                state.error = null;
            })
            .addCase(createProvider.rejected, (state, action) => {
                state.loading = 'failed';
                state.error = action.error.message ?? 'An error occurred.';
            })
            .addCase(getProviderById.pending, (state) => {
                state.loading = 'pending';
            })
            .addCase(getProviderById.fulfilled, (state, action) => {
                state.loading = 'succeeded';
                const responseData = action.payload?.result || action.payload;
                if (responseData) {
                    state.selectedProvider = {
                        entity: responseData.entity || {},
                        facilityList: responseData.facilityList || [],
                    };
                }
                state.error = null;
            })
            .addCase(getProviderById.rejected, (state, action) => {
                state.loading = 'failed';
                state.error = action.error.message ?? 'An error occurred.';
            })
            .addCase(updateProvider.pending, (state) => {
                state.loading = 'pending';
            })
            .addCase(updateProvider.fulfilled, (state) => {
                state.loading = 'succeeded';
                state.error = null;
            })
            .addCase(updateProvider.rejected, (state, action) => {
                state.loading = 'failed';
                state.error = action.error.message ?? 'An error occurred.';
            })
            .addCase(getServiceProviders.pending, (state) => {
                state.loading = 'pending';
            })
            .addCase(getServiceProviders.fulfilled, (state, action) => {
                state.loading = 'succeeded';
                const responseData = action.payload?.result || action.payload;
                state.serviceProviders = Array.isArray(responseData)
                    ? responseData
                    : [];
                state.error = null;
            })
            .addCase(getServiceProviders.rejected, (state, action) => {
                state.loading = 'failed';
                state.error = action.error.message ?? 'An error occurred.';
            })
            .addCase(getPlanList.pending, (state) => {
                state.loading = 'pending';
            })
            .addCase(getPlanList.fulfilled, (state, action) => {
                state.loading = 'succeeded';
                const responseData = action.payload?.result || action.payload;
                state.planData = {
                    list: responseData?.list || [],
                    totalPages: responseData?.totalPages || 0,
                    totalRecords: responseData?.totalRecords || 0,
                };
                state.error = null;
            })
            .addCase(getPlanList.rejected, (state, action) => {
                state.loading = 'failed';
                state.error = action.error.message ?? 'An error occurred.';
            })
            .addCase(createPlan.pending, (state) => {
                state.loading = 'pending';
            })
            .addCase(createPlan.fulfilled, (state) => {
                state.loading = 'succeeded';
                state.error = null;
            })
            .addCase(createPlan.rejected, (state, action) => {
                state.loading = 'failed';
                state.error = action.error.message ?? 'An error occurred.';
            })
            .addCase(getPlanById.pending, (state) => {
                state.loading = 'pending';
            })
            .addCase(getPlanById.fulfilled, (state, action) => {
                state.loading = 'succeeded';
                const responseData = action.payload?.result || action.payload;
                if (responseData) {
                    state.selectedPlan = responseData;
                }
                state.error = null;
            })
            .addCase(getPlanById.rejected, (state, action) => {
                state.loading = 'failed';
                state.error = action.error.message ?? 'An error occurred.';
            })
            .addCase(updatePlan.pending, (state) => {
                state.loading = 'pending';
            })
            .addCase(updatePlan.fulfilled, (state) => {
                state.loading = 'succeeded';
                state.error = null;
            })
            .addCase(updatePlan.rejected, (state, action) => {
                state.loading = 'failed';
                state.error = action.error.message ?? 'An error occurred.';
            })
            .addCase(getBeneficiaryList.pending, (state) => {
                state.loading = 'pending';
            })
            .addCase(getBeneficiaryList.fulfilled, (state, action) => {
                state.loading = 'succeeded';
                const responseData = action.payload?.result || action.payload;
                state.beneficiaryData = {
                    list: responseData?.list || [],
                    totalPages: responseData?.totalPages || 0,
                    totalRecords: responseData?.totalRecords || 0,
                };
                state.error = null;
            })
            .addCase(getBeneficiaryList.rejected, (state, action) => {
                state.loading = 'failed';
                state.error = action.error.message ?? 'An error occurred.';
            })
            .addCase(createBeneficiary.pending, (state) => {
                state.loading = 'pending';
            })
            .addCase(createBeneficiary.fulfilled, (state) => {
                state.loading = 'succeeded';
                state.error = null;
            })
            .addCase(createBeneficiary.rejected, (state, action) => {
                state.loading = 'failed';
                state.error = action.error.message ?? 'An error occurred.';
            })
            .addCase(getBeneficiaryById.pending, (state) => {
                state.loading = 'pending';
            })
            .addCase(getBeneficiaryById.fulfilled, (state, action) => {
                state.loading = 'succeeded';
                const responseData = action.payload?.result || action.payload;
                if (responseData) {
                    state.selectedBeneficiary = responseData;
                }
                state.error = null;
            })
            .addCase(getBeneficiaryById.rejected, (state, action) => {
                state.loading = 'failed';
                state.error = action.error.message ?? 'An error occurred.';
            })
            .addCase(updateBeneficiary.pending, (state) => {
                state.loading = 'pending';
            })
            .addCase(updateBeneficiary.fulfilled, (state) => {
                state.loading = 'succeeded';
                state.error = null;
            })
            .addCase(updateBeneficiary.rejected, (state, action) => {
                state.loading = 'failed';
                state.error = action.error.message ?? 'An error occurred.';
            })
            .addCase(bulkUploadBeneficiaries.pending, (state) => {
                state.loading = 'pending';
            })
            .addCase(bulkUploadBeneficiaries.fulfilled, (state) => {
                state.loading = 'succeeded';
                state.error = null;
            })
            .addCase(bulkUploadBeneficiaries.rejected, (state, action) => {
                state.loading = 'failed';
                state.error = action.error.message ?? 'An error occurred.';
            })
            .addCase(getBeneficiariesByPlan.pending, (state) => {
                state.loading = 'pending';
            })
            .addCase(getBeneficiariesByPlan.fulfilled, (state, action) => {
                state.loading = 'succeeded';
                const responseData = action.payload?.result || action.payload;
                // API returns array directly, convert to list format
                const beneficiariesList = Array.isArray(responseData)
                    ? responseData
                    : [];
                state.beneficiaryData = {
                    list: beneficiariesList,
                    totalPages: 1,
                    totalRecords: beneficiariesList.length,
                };
                state.error = null;
            })
            .addCase(getBeneficiariesByPlan.rejected, (state, action) => {
                state.loading = 'failed';
                state.error = action.error.message ?? 'An error occurred.';
            })
            .addCase(checkBenefitEligibilityByEMR.pending, (state) => {
                state.loading = 'pending';
            })
            .addCase(checkBenefitEligibilityByEMR.fulfilled, (state, action) => {
                state.loading = 'succeeded';
                const responseData = action.payload?.result || action.payload;
                state.benefitEligibility = responseData || null;
                state.error = null;
            })
            .addCase(checkBenefitEligibilityByEMR.rejected, (state, action) => {
                state.loading = 'failed';
                state.error = action.error.message ?? 'An error occurred.';
                state.benefitEligibility = null;
            })
            .addCase(getInsuranceProviderDashboard.pending, (state) => {
                state.loading = 'pending';
            })
            .addCase(getInsuranceProviderDashboard.fulfilled, (state, action) => {
                state.loading = 'succeeded';
                const responseData = action.payload?.result || action.payload;
                state.providerDashboard = responseData || null;
                state.error = null;
            })
            .addCase(getInsuranceProviderDashboard.rejected, (state, action) => {
                state.loading = 'failed';
                state.error = action.error.message ?? 'An error occurred.';
                state.providerDashboard = null;
            })
            .addCase(getInsuranceProviderBeneficiaries.pending, (state) => {
                state.loading = 'pending';
            })
            .addCase(getInsuranceProviderBeneficiaries.fulfilled, (state, action) => {
                state.loading = 'succeeded';
                const responseData = action.payload?.result || action.payload;
                state.providerBeneficiaries = {
                    list: responseData?.list || [],
                    totalPages: responseData?.totalPages || 0,
                    totalRecords: responseData?.totalRecords || 0,
                };
                state.error = null;
            })
            .addCase(getInsuranceProviderBeneficiaries.rejected, (state, action) => {
                state.loading = 'failed';
                state.error = action.error.message ?? 'An error occurred.';
            })
            .addCase(getInsuranceProviderPlans.pending, (state) => {
                state.loading = 'pending';
            })
            .addCase(getInsuranceProviderPlans.fulfilled, (state, action) => {
                state.loading = 'succeeded';
                const responseData = action.payload?.result || action.payload;
                state.providerPlans = {
                    list: responseData?.list || [],
                    totalPages: responseData?.totalPages || 0,
                    totalRecords: responseData?.totalRecords || 0,
                };
                state.error = null;
            })
            .addCase(getInsuranceProviderPlans.rejected, (state, action) => {
                state.loading = 'failed';
                state.error = action.error.message ?? 'An error occurred.';
            })
            .addCase(getInsuranceProviderBeneficiaryUsage.pending, (state) => {
                state.loading = 'pending';
            })
            .addCase(getInsuranceProviderBeneficiaryUsage.fulfilled, (state, action) => {
                state.loading = 'succeeded';
                const responseData = action.payload?.result || action.payload;
                state.providerBeneficiaryUsage = {
                    list: responseData?.list || [],
                    totalPages: responseData?.totalPages || 0,
                    totalRecords: responseData?.totalRecords || 0,
                };
                state.error = null;
            })
            .addCase(getInsuranceProviderBeneficiaryUsage.rejected, (state, action) => {
                state.loading = 'failed';
                state.error = action.error.message ?? 'An error occurred.';
            });
    },
});

export const { onInsurancePageChange, onChangeInsuranceType } =
    insurance.actions;

export default insurance.reducer;
